/**
 * When the Map page may move the map by itself.
 *
 * It used to fit every marker into view on every render. The page re-renders
 * on its one-minute refresh, on picking an engineer, on almost anything, so
 * someone zoomed in on a farm was thrown back out to the whole country within
 * a minute (2026-09-14).
 *
 * The rule now: the map fits its markers when they first arrive, and again
 * when it is asked to show something different (a layer switched, a filter
 * changed), until the user pans or zooms it. From then on the view is theirs
 * until they press Fit all.
 */

/**
 * What the map is showing, as one string: the visible layers and the query the
 * markers were fetched with. A refresh of the same question gives the same
 * string, even though engineers have moved and tickets have come and gone.
 *
 * @param {Object<string, boolean>} layers - visible layers by name
 * @param {string} dataScope - the query string the markers were fetched with
 * @returns {string}
 */
export function mapFitScopeKey(layers, dataScope) {
  const visible = Object.keys(layers).filter(name => layers[name]).sort();
  return `${visible.join(',')}|${dataScope}`;
}

/**
 * Should the map fit itself to its markers now? Only when there is something
 * to fit, the user has not moved the map, and it has not already been fitted
 * to what it is showing.
 *
 * @param {Object} state
 * @param {boolean} state.hasPositions - at least one marker is on the map
 * @param {boolean} state.userMoved - the user has panned or zoomed the map
 * @param {string} state.scopeKey - what the map shows now (mapFitScopeKey)
 * @param {?string} state.fittedScopeKey - what it was last fitted to; null before the first fit
 * @returns {boolean}
 */
export function shouldAutoFit({ hasPositions, userMoved, scopeKey, fittedScopeKey }) {
  if (!hasPositions || userMoved) return false;
  return scopeKey !== fittedScopeKey;
}
